import {
  Component,
  Host,
  Prop,
  Element,
  State,
  getAssetPath,
  Method,
  h
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { config } from "../../common/config";

import { Locale } from "../../common/locale";
import { FormSubmitResult } from "../../common/types";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";
import {
  ControlValidation,
  validateControls
} from "../../common/form-validation";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tab",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const FILE_ICON = getIconPath({
  category: "gemini-tools",
  name: "file",
  colorType: "primary"
});

const MENU_DELETE = getIconPath({
  category: "menus",
  name: "delete",
  colorType: "primary"
});

@Component({
  tag: "gx-ide-json-import",
  styleUrl: "json-import.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/json-import"]
})
export class GxIdeJsonImport {
  #componentLocale: any;
  #data: JsonImportData = {
    name: "",
    description: "",
    assumeVarcharForNull: false,
    parentId: "",
    json: ""
  };
  #radioOptionsModel: RadioGroupItemModel[] = [];
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/json-import/shortcuts.json`);

  @Element() el: HTMLGxIdeJsonImportElement;
  #checkBoxEl!: HTMLChCheckboxElement;
  #chShortcutsEl: HTMLChShortcutsElement;
  #descriptionEl!: HTMLChEditElement;
  #fileInputHiddenEl!: HTMLInputElement;
  #moduleEntitySelector!: HTMLGxIdeEntitySelectorElement;
  #nameEl!: HTMLChEditElement;
  #radioGroup!: HTMLChRadioGroupRenderElement;

  @State() fileName: string;
  @State() radioGroupValue: string = "file";
  @State() isFormValid: boolean = false;
  @State() jsonContent: string = "";
  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * Callback that must be invoked when the user want to cancel the operation
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  /**
   * Callback that must be invoked when the user confirms the creation of the SDT. It receives as parameters the data necessary to create the SDT.
   */
  @Prop() readonly confirmCallback!: (
    data: JsonImportData
  ) => Promise<FormSubmitResult>;

  /**
   * Default value for Module/Folder field
   */
  @Prop() readonly defaultParent: EntityData;

  /**
   * Default value for Module/Folder field
   */
  @Prop() readonly defaultRadioValue: string;

  /**
   * Actual value for Module/Folder field
   */
  @Prop() readonly parent: EntityData;

  /**
   * Callback invoked when the action is executed in the Module/Folder filter. It returns the information of the selected object (id and name) or 'undefined' if it was canceled.
   */
  @Prop() readonly selectModuleCallback: SelectModuleCallback;

  /**
   * Callback invoked when the input mode changes between file and text.
   */
  @Prop() readonly modeChangeCallback?: (
    mode: "file" | "text"
  ) => Promise<void>;

  /**
   * Callback invoked when a file is selected or removed.
   */
  @Prop() readonly fileChangeCallback?: (file: File | null) => Promise<void>;

  /**
   * Callback para validar el nombre del objeto. Debe retornar true si el nombre es válido, false en caso contrario.
   */
  @Prop() readonly validateNameCallback?: (name: string) => boolean;

  /**
   * Callback para validar el JSON. Debe retornar true si el JSON es válido, false en caso contrario.
   */
  @Prop() readonly validateJSONCallback?: (json: string) => boolean;

  /**
   * Callback invocado cuando el contenido del JSON cambia
   */
  @Prop() readonly onJsonContentChange?: (content: string) => void;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#radioOptionsModel = [
      {
        value: "file",
        caption: this.#componentLocale.header.filePathLabel
      },
      { value: "text", caption: this.#componentLocale.main.jsonAreaLabel }
    ];
  }

  componentDidLoad() {
    this.#initializeValidatableControls();
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    this.#chShortcutsEl.suspend = suspendShortcuts;
  }

  #initializeValidatableControls = () => {
    const validatableControls: HTMLElement[] = [
      this.#nameEl,
      this.#moduleEntitySelector
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #validateForm = () => {
    const name = this.#nameEl?.value?.trim() || "";
    const json =
      this.radioGroupValue === "file" ? this.fileName : this.jsonContent;

    const isNameValid = this.validateNameCallback
      ? this.validateNameCallback(name)
      : Boolean(name);

    const isJsonValid =
      this.radioGroupValue === "file"
        ? Boolean(this.fileName)
        : this.validateJSONCallback
          ? this.validateJSONCallback(json)
          : false;

    this.isFormValid = isNameValid && isJsonValid;
  };

  #nameChangedHandler = () => {
    this.#validateForm();
  };

  #changeRadioHandler = () => {
    this.radioGroupValue = this.#radioGroup.value;
    if (this.radioGroupValue === "file") {
      this.#checkBoxEl.value = "false";
    } else {
      this.#data.json = null;
      this.#removeFileSelection();
      this.#validateForm();
    }
    this.modeChangeCallback?.(this.radioGroupValue as "file" | "text");
  };

  #createHandler = () => {
    this.#data.name = this.#nameEl.value;
    this.#data.description = this.#descriptionEl.value;
    this.#data.parentId = this.#moduleEntitySelector.value?.id;
    this.#data.assumeVarcharForNull = this.#checkBoxEl.value === "true";
    this.confirmCallback(this.#data).then(
      (formSubmitResult: FormSubmitResult) => {
        this.validatableControls = validateControls(
          formSubmitResult,
          this.validatableControls
        );
      }
    );
  };

  #removeFileSelection = () => {
    this.#data.json = null;
    this.#fileInputHiddenEl.value = null;
    this.fileName = null;
    this.fileChangeCallback?.(null);
    this.#validateForm();
  };

  #selectFileInputChangedHandler = (event: InputEvent) => {
    const target = event.target as HTMLInputElement;
    if (target.files?.length > 0 && this.radioGroupValue === "file") {
      this.#data.json = target.files[0];
      this.fileName = target.files[0].name;
      this.fileChangeCallback?.(target.files[0]);
      this.#validateForm();
    }
  };

  #selectFileInputHandler = () => {
    this.#fileInputHiddenEl.click();
  };

  #handleEditorChange = () => {
    this.#validateForm();
  };

  #handleSlotChange = (e: Event) => {
    const elements = (e.target as HTMLSlotElement).assignedElements();
    if (elements.length > 0) {
      const element = elements[0];
      element.addEventListener("json-content-change", ((event: CustomEvent) => {
        if (this.radioGroupValue === "text") {
          this.jsonContent = event.detail.content;
          this.#handleEditorChange();
        }
      }) as EventListener);
    }
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header class="header field-group spacing-body dialog-header-with-border">
            <div class="field-group field-group-name-description-module">
              <label
                // name
                class="label name-label"
                htmlFor="input-name"
              >
                {this.#componentLocale.header.nameFieldLabel}
              </label>
              <ch-edit
                class="input name-input"
                id="input-name"
                ref={(el: HTMLChEditElement) =>
                  (this.#nameEl = el as HTMLChEditElement)
                }
                onInput={this.#nameChangedHandler}
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#nameEl)}

              <label
                // description
                class="label description-label"
                htmlFor="input-description"
              >
                {this.#componentLocale.header.descriptionFieldLabel}
              </label>
              <ch-edit
                class="input description-input"
                id="input-description"
                ref={(el: HTMLChEditElement) =>
                  (this.#descriptionEl = el as HTMLChEditElement)
                }
              ></ch-edit>

              <label
                // module/folder
                class="label module-label"
                htmlFor="input-module"
              >
                {this.#componentLocale.header.moduleFolderLabel}
              </label>
              <gx-ide-entity-selector
                class="module module-entity-selector"
                id="module"
                value={this.defaultParent}
                ref={(el: HTMLGxIdeEntitySelectorElement) =>
                  (this.#moduleEntitySelector =
                    el as HTMLGxIdeEntitySelectorElement)
                }
                labelPosition="none"
                defaultValue={this.defaultParent}
                selectEntityCallback={this.selectModuleCallback}
              ></gx-ide-entity-selector>
              {this.#evaluateTooltipRender(this.#moduleEntitySelector)}
            </div>

            <div class="radio-file-input-wrapper field-group">
              <ch-radio-group-render
                // file url/text
                class="radio-group"
                model={this.#radioOptionsModel}
                onChange={this.#changeRadioHandler}
                value={this.defaultRadioValue}
                ref={(el: HTMLChRadioGroupRenderElement) =>
                  (this.#radioGroup = el as HTMLChRadioGroupRenderElement)
                }
              ></ch-radio-group-render>
              <div class="field field-inline field-file-input">
                <ch-edit
                  autoFocus
                  id="file-name"
                  class="input"
                  part="file-name"
                  type="text"
                  value={this.fileName}
                  readonly
                  placeholder={this.#componentLocale.header.fileNamePlaceholder}
                  startImgSrc={FILE_ICON}
                  disabled={this.radioGroupValue !== "file"}
                ></ch-edit>
                <input
                  hidden
                  type="file"
                  accept=".json"
                  onChange={this.#selectFileInputChangedHandler}
                  ref={(el: HTMLInputElement) =>
                    (this.#fileInputHiddenEl = el as HTMLInputElement)
                  }
                />
                <div class="buttons-spacer">
                  <button
                    aria-label={
                      this.#componentLocale.header.removeFileSelection
                    }
                    title={this.#componentLocale.header.removeFileSelection}
                    id="reset-all-button"
                    class="button-tertiary button-icon-only"
                    part="reset-all-button"
                    onClick={this.#removeFileSelection}
                    disabled={this.radioGroupValue !== "file"}
                  >
                    <ch-image class="icon-m" src={MENU_DELETE}></ch-image>
                  </button>

                  <button
                    id="select-file-load-button"
                    class="button-primary"
                    part="select-file-load-button"
                    onClick={this.#selectFileInputHandler}
                    disabled={this.radioGroupValue !== "file"}
                  >
                    {this.#componentLocale.header.selectfileButton}
                  </button>
                </div>
              </div>
            </div>
          </header>
          <div class="main field-group">
            <div class="json-area">
              <div class="slotted-container scrollable">
                <slot
                  name="JsonTextEditor"
                  onSlotchange={this.#handleSlotChange}
                ></slot>
              </div>
            </div>
            <ch-checkbox
              class="checkbox assume-varchar-checkbox"
              caption={this.#componentLocale.main.checkBoxLabel}
              checkedValue="true"
              ref={(el: HTMLChCheckboxElement) =>
                (this.#checkBoxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </div>

          <footer class="dialog-footer dialog-footer-with-border spacing-body-block-end spacing-body-inline">
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                id="button-cancel"
                onClick={this.cancelCallback}
                part="button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                class="button-primary"
                id="button-ok"
                onClick={this.#createHandler}
                part="button-ok"
                disabled={!this.isFormValid}
              >
                {this.#componentLocale.footer.btnOk}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type JsonImportData = {
  name: string;
  description: string;
  parentId: string;
  json: File | string;
  assumeVarcharForNull: boolean;
};

export type EntityData = {
  id: string;
  name: string;
  iconSrc?: string;
};

export type SelectModuleCallback = () => Promise<EntityData | undefined>;
